import {
  Component, EventEmitter, Input, OnDestroy, OnInit, Output,
  ChangeDetectionStrategy, ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription, interval } from 'rxjs';
import { take, timeout } from 'rxjs/operators';
import { FiservPosPaymentService } from '../../services/fiserv-pos-payment.service';
import {
  FiservAuthorizationResponse,
  FiservClinicTerminal,
} from '../../../models/fiserv.models';

@Component({
  selector: 'fiserv-pos-payment',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './fiserv-pos-payment.component.html',
  styleUrls: ['./fiserv-pos-payment.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FiservPosPaymentComponent implements OnInit, OnDestroy {
  /** Pre-set amount in CENTS. If provided, Step 2 is skipped. */
  @Input() amountCents?: number;

  /** Pre-set HSN. If provided, Step 1 is skipped. */
  @Input() hsn?: string;

  /** Payment type for the charge ('card' | 'manual'). Default: 'card'. */
  @Input() paymentType: 'card' | 'manual' = 'card';

  /** Emitted when payment is approved or declined — parent decides what to do. */
  @Output() paymentComplete = new EventEmitter<FiservAuthorizationResponse>();

  /** Emitted when user clicks Done/Cancel without completing payment. */
  @Output() paymentCancelled = new EventEmitter<void>();

  currentStep: 1 | 2 | 3 | 4 = 1;

  // Step 1 — Terminal selection
  terminals: FiservClinicTerminal[] = [];
  loadingTerminals = false;
  selectedHsn: string | null = null;
  selectedTerminalLabel: string | null = null;
  pingStatus: Record<string, boolean | null> = {}; // hsn → true/false/null(unknown)
  pingingHsn: string | null = null;

  // Step 2 — Amount
  saleAmountCents = 0;

  // Step 3 — Processing
  elapsedSeconds = 0;
  private elapsedTimer: Subscription | null = null;
  private paymentSub: Subscription | null = null;

  // Step 4 — Result
  authResponse: FiservAuthorizationResponse | null = null;
  paymentSucceeded = false;
  errorMessage: string | null = null;
  private paymentEventEmitted = false;

  private subs = new Subscription();

  constructor(
    private posService: FiservPosPaymentService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (this.amountCents != null && this.amountCents > 0) {
      this.saleAmountCents = this.amountCents;
    }
    if (this.hsn) {
      this.selectedHsn = this.hsn;
      this.currentStep = this.saleAmountCents > 0 ? 3 : 2;
      if (this.saleAmountCents > 0) this.submitPayment();
    } else {
      this.loadTerminals();
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.stopTimers();
  }

  // ─── Step 1 ───────────────────────────────────────────────────────────────

  loadTerminals(): void {
    this.loadingTerminals = true;
    this.posService.getTerminals().pipe(take(1)).subscribe({
      next: terminals => {
        this.terminals = terminals.filter(t => t.isActive);
        if (this.terminals.length >= 1) {
          this.selectedHsn = this.terminals[0].hsn;
          this.selectedTerminalLabel = this.terminals[0].label;
        }
        this.loadingTerminals = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loadingTerminals = false;
        this.cdr.markForCheck();
      },
    });
  }

  selectTerminal(terminal: FiservClinicTerminal): void {
    this.selectedHsn = terminal.hsn;
    this.selectedTerminalLabel = terminal.label;
    this.cdr.markForCheck();
  }

  pingTerminal(hsn: string): void {
    this.pingingHsn = hsn;
    this.pingStatus[hsn] = null;
    this.cdr.markForCheck();
    this.posService.pingTerminal(hsn).pipe(take(1)).subscribe({
      next: r => {
        this.pingStatus[hsn] = r.connected;
        this.pingingHsn = null;
        this.cdr.markForCheck();
      },
      error: () => {
        this.pingStatus[hsn] = false;
        this.pingingHsn = null;
        this.cdr.markForCheck();
      },
    });
  }

  goToStep2(): void {
    if (!this.selectedHsn) return;
    if (this.saleAmountCents > 0) {
      this.currentStep = 3;
      this.cdr.markForCheck();
      this.submitPayment();
    } else {
      this.currentStep = 2;
      this.cdr.markForCheck();
    }
  }

  backToStep1(): void { this.currentStep = 1; this.cdr.markForCheck(); }

  // ─── Step 2 ───────────────────────────────────────────────────────────────

  get displayAmountDollars(): number {
    return this.saleAmountCents / 100;
  }

  set displayAmountDollars(dollars: number) {
    this.saleAmountCents = Math.round(dollars * 100);
  }

  startPayment(): void {
    if (!this.saleAmountCents || this.saleAmountCents <= 0) return;
    this.currentStep = 3;
    this.cdr.markForCheck();
    this.submitPayment();
  }

  // ─── Step 3 ───────────────────────────────────────────────────────────────

  private submitPayment(): void {
    this.elapsedSeconds = 0;
    this.errorMessage = null;

    this.elapsedTimer = interval(1000).subscribe(() => {
      this.elapsedSeconds++;
      this.cdr.markForCheck();
    });
    this.subs.add(this.elapsedTimer);

    const req = {
      hsn: this.selectedHsn!,
      amount: this.saleAmountCents,   // CENTS integer
    };

    const payment$ = this.paymentType === 'manual'
      ? this.posService.processManualPayment(req)
      : this.posService.processCardPayment(req);

    this.paymentSub = payment$.pipe(
      take(1),
      timeout(90000)
    ).subscribe({
      next: response => {
        this.stopTimers();
        this.authResponse = response;
        this.paymentSucceeded =
          response.respStat === 'A' ||
          response.status === 'approved' ||
          response.status === 'succeeded';
        this.currentStep = 4;
        this.cdr.markForCheck();
        if (this.paymentSucceeded) this.emitPaymentComplete();
      },
      error: err => {
        this.stopTimers();
        this.errorMessage = err?.name === 'TimeoutError'
          ? 'The terminal did not respond in time. Please check the device and try again.'
          : (err?.error?.details ?? err?.message ?? 'Payment failed. Please try again.');
        this.paymentSucceeded = false;
        this.currentStep = 4;
        this.cdr.markForCheck();
      },
    });
    this.subs.add(this.paymentSub);
  }

  private stopTimers(): void {
    this.elapsedTimer?.unsubscribe();
    this.paymentSub?.unsubscribe();
    this.elapsedTimer = null;
    this.paymentSub = null;
  }

  // ─── Step 4 ───────────────────────────────────────────────────────────────

  get displayAmountLabel(): string {
    return `$${(this.saleAmountCents / 100).toFixed(2)}`;
  }

  done(): void { this.emitPaymentComplete(); }

  cancel(): void { this.paymentCancelled.emit(); }

  tryAgain(): void {
    this.stopTimers();
    this.authResponse = null;
    this.errorMessage = null;
    this.paymentSucceeded = false;
    this.paymentEventEmitted = false;
    this.elapsedSeconds = 0;
    if (this.hsn) {
      this.currentStep = this.amountCents ? 3 : 2;
      if (this.amountCents) this.submitPayment();
    } else {
      this.currentStep = 1;
      this.loadTerminals();
    }
    this.cdr.markForCheck();
  }

  private emitPaymentComplete(): void {
    if (!this.paymentEventEmitted) {
      this.paymentEventEmitted = true;
      this.paymentComplete.emit(
        this.authResponse ?? { status: this.paymentSucceeded ? 'approved' : 'declined' }
      );
    }
  }

  trackByHsn(_i: number, t: FiservClinicTerminal): string { return t.hsn; }
}
